import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "@/components/templates/page-layout";
import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb, deleteCampagneCascade, type OfflineSite, type OfflineCampagne } from "@/core/offline/db";
import { deleteWithOfflineFallback } from "@/core/offline/delete-with-fallback";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/molecules/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Lock, ChevronRight, ChevronDown, MapPin, Trash2, Download } from "lucide-react";

interface SiteGroup {
  site: OfflineSite;
  campagnes: OfflineCampagne[]; // triées par date desc
}

/**
 * Page /campagnes — Archive historique des campagnes clôturées.
 * Groupées par site (accordéon) : 1 ligne par site avec badge "N campagnes",
 * dépliable pour voir chaque campagne individuellement avec date + bouton supprimer.
 */
export default function CampagnesPage() {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [campagneToDelete, setCampagneToDelete] = useState<OfflineCampagne | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const siteGroups = useLiveQuery(async () => {
    const campagnes = await offlineDb.campagnes
      .filter((c) => c.status === "cloturee")
      .toArray();
    if (campagnes.length === 0) return [];

    const siteIds = [...new Set(campagnes.map((c) => c.siteId))];
    const sites = await offlineDb.sites.where("id").anyOf(siteIds).toArray();
    const siteById = new Map(sites.map((s) => [s.id, s]));

    const groupsBySiteId = new Map<string, SiteGroup>();
    for (const c of campagnes) {
      const site = siteById.get(c.siteId);
      if (!site) continue;
      const group = groupsBySiteId.get(c.siteId) ?? { site, campagnes: [] };
      group.campagnes.push(c);
      groupsBySiteId.set(c.siteId, group);
    }

    // Tri des campagnes de chaque site par date desc
    for (const group of groupsBySiteId.values()) {
      group.campagnes.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    }

    // Tri des sites par date de leur plus récente campagne clôturée
    return [...groupsBySiteId.values()].sort(
      (a, b) =>
        new Date(b.campagnes[0].date).getTime() - new Date(a.campagnes[0].date).getTime()
    );
  }, []) ?? [];

  function toggleExpanded(siteId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  }

  // Scroll lock + Escape pour le modal de suppression
  useEffect(() => {
    if (!campagneToDelete) return;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !deleting) {
        setCampagneToDelete(null);
        setDeleteError("");
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [campagneToDelete, deleting]);

  async function handleConfirmDelete() {
    if (!campagneToDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteWithOfflineFallback(
        "campagne",
        campagneToDelete.id,
        `/api/campagnes/${campagneToDelete.id}`,
        () => deleteCampagneCascade(campagneToDelete.id)
      );
      setCampagneToDelete(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <PageLayout title="Campagnes clôturées">
      {siteGroups.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          <Lock className="size-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Aucune campagne clôturée pour l'instant.</p>
          <p className="text-xs mt-1">
            Les campagnes apparaissent ici dès qu'elles sont clôturées.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {siteGroups.map(({ site, campagnes }) => {
            const isOpen = expanded.has(site.id);
            const count = campagnes.length;

            return (
              <div
                key={site.id}
                className="bg-card rounded-xl border border-border overflow-hidden"
              >
                {/* En-tête cliquable : toggle expand */}
                <button
                  onClick={() => toggleExpanded(site.id)}
                  className="w-full flex items-center gap-3 p-4 hover:bg-muted/40 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <Lock className="size-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold truncate">{site.name}</span>
                      <Badge variant="secondary" className="text-xs shrink-0">
                        {count} campagne{count > 1 ? "s" : ""}
                      </Badge>
                    </div>
                    {site.address && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <MapPin className="size-3" />
                        <span className="truncate">{site.address}</span>
                      </div>
                    )}
                  </div>
                  {isOpen ? (
                    <ChevronDown className="size-5 text-muted-foreground/60 shrink-0" />
                  ) : (
                    <ChevronRight className="size-5 text-muted-foreground/60 shrink-0" />
                  )}
                </button>

                {/* Contenu déplié : liste des campagnes */}
                {isOpen && (
                  <div className="border-t border-border">
                    {campagnes.map((c, idx) => (
                      <div
                        key={c.id}
                        className={`flex items-center gap-3 px-4 py-3 ${idx > 0 ? "border-t border-border/50" : ""}`}
                      >
                        <button
                          onClick={() => navigate(`/sites/${site.id}?campagneId=${c.id}`)}
                          className="flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                          title="Ouvrir la campagne (consultation)"
                        >
                          <div className="text-sm font-medium">
                            Campagne du{" "}
                            {new Date(c.date).toLocaleDateString("fr-FR", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            Clôturée le{" "}
                            {new Date(c.updatedAt).toLocaleDateString("fr-FR", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </div>
                        </button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="shrink-0"
                          onClick={() => navigate(`/exports?siteId=${site.id}&campagneId=${c.id}`)}
                          title="Exporter cette campagne"
                        >
                          <Download className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:bg-destructive/10 shrink-0"
                          onClick={() => setCampagneToDelete(c)}
                          title="Supprimer cette campagne"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal confirmation suppression */}
      <ConfirmDialog
        open={!!campagneToDelete}
        title="Supprimer cette campagne ?"
        description={
          campagneToDelete ? (
            <>
              La campagne du{" "}
              <strong>
                {new Date(campagneToDelete.date).toLocaleDateString("fr-FR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </strong>{" "}
              et toutes ses mesures seront définitivement supprimées. Les pins
              sur les plans ne seront pas touchés (ils sont partagés entre toutes
              les campagnes du site). Action irréversible.
            </>
          ) : undefined
        }
        error={deleteError}
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setCampagneToDelete(null);
          setDeleteError("");
        }}
      />
    </PageLayout>
  );
}
